import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { 
  MessageCircle, X, Send, Volume2, VolumeX, Loader2, 
  BookOpen, Lightbulb, HelpCircle, FileText, Play, Pause,
  Mic, Square, Sparkles, Settings, Check, Lock, Zap
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { LinceMascot } from './TuPlazaIALogo';
import { CreditUpsellModal, useAICredits, LowBalanceWarning } from './CreditUpsellModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TUTOR_TOPICS = [
  { id: 'explain', label: 'Explícame un tema', icon: BookOpen, prompt: 'Explícame de forma clara y didáctica el tema: ' },
  { id: 'doubt', label: 'Tengo una duda', icon: HelpCircle, prompt: 'Tengo una duda sobre: ' },
  { id: 'summary', label: 'Resumen rápido', icon: FileText, prompt: 'Dame un resumen conciso de: ' },
  { id: 'tips', label: 'Técnicas estudio', icon: Lightbulb, prompt: 'Dame técnicas de memorización para: ' },
];

const INTERACTION_MODES = [
  { id: 'text_only', label: 'Solo texto', icon: '⌨️', cost: 5, desc: 'Escribe y lee' },
  { id: 'text_with_audio', label: 'Texto + Audio', icon: '🔊', cost: 15, desc: 'Escribe y escucha' },
  { id: 'voice_with_text', label: 'Voz → Texto', icon: '🎤', cost: 8, desc: 'Habla y lee' },
  { id: 'voice_only', label: 'Solo voz', icon: '🎙️', cost: 20, desc: 'Habla y escucha' },
];

const TutorChat = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showModeSelector, setShowModeSelector] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [interactionMode, setInteractionMode] = useState('text_only');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [creditsUsed, setCreditsUsed] = useState(0);
  
  // AI Credits state
  const { credits: aiCredits, loading: loadingCredits, refetch: refetchCredits } = useAICredits();
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [upsellData, setUpsellData] = useState({ required: 0, message: '' });
  
  // User and plan state
  const [user, setUser] = useState(null);
  const [hasTutorAccess, setHasTutorAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  
  // Pages where TutorChat should NOT appear (public/landing pages)
  const excludedPaths = ['/', '/auth', '/pricing', '/aviso-legal', '/privacidad', '/contacto', '/logos', '/auth/callback'];
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const recordingTimerRef = useRef(null);

  // Check user access on mount, when auth changes, and when route changes
  useEffect(() => {
    const checkTutorAccess = async () => {
      const token = localStorage.getItem('session_token');
      if (!token) {
        setHasTutorAccess(false);
        setCheckingAccess(false);
        return;
      }

      try {
        // Get user profile to check plan
        const response = await axios.get(`${API}/profile/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const userData = response.data;
        setUser(userData);
        
        // Get plan from subscription object
        const userPlan = userData.subscription?.plan || 'basic';
        
        // Check if user's plan has tutor access
        const planResponse = await axios.get(`${API}/plans/${userPlan}`);
        const planData = planResponse.data;
        
        setHasTutorAccess(planData.has_tutor_ia === true);
      } catch (error) {
        console.error('Error checking tutor access:', error);
        setHasTutorAccess(false);
      } finally {
        setCheckingAccess(false);
      }
    };

    // Re-check access when pathname changes (after login redirect)
    setCheckingAccess(true);
    checkTutorAccess();
    
    // Re-check when storage changes (login/logout)
    const handleStorageChange = () => {
      setCheckingAccess(true);
      checkTutorAccess();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [location.pathname]); // Re-run when route changes

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Cleanup recording timer
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await handleVoiceInput(blob);
      };
      
      setAudioChunks([]);
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      
      toast.info('🎤 Grabando... Pulsa de nuevo para enviar');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('No se pudo acceder al micrófono. Revisa los permisos.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const handleVoiceInput = async (audioBlob) => {
    if (!audioBlob || audioBlob.size === 0) {
      toast.error('No se grabó audio. Intenta de nuevo.');
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      // Determine if we want audio response based on mode
      const wantAudioResponse = interactionMode === 'voice_only' || interactionMode === 'text_with_audio';
      
      if (interactionMode === 'voice_only' || interactionMode === 'voice_with_text') {
        // Use voice-to-voice endpoint
        formData.append('include_audio_response', wantAudioResponse.toString());
        
        const response = await axios.post(
          `${API}/tutor/voice-chat`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        // Add user message (transcribed)
        const userMessage = { 
          role: 'user', 
          content: response.data.user_text,
          isVoice: true,
          timestamp: new Date() 
        };
        setMessages(prev => [...prev, userMessage]);
        
        // Add AI response
        const aiMessage = { 
          role: 'assistant', 
          content: response.data.response,
          audio_url: response.data.audio_url,
          credits_used: response.data.credits_used,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiMessage]);
        setCreditsUsed(prev => prev + response.data.credits_used);
        
        // Auto-play audio if available
        if (response.data.audio_url) {
          playAudio(response.data.audio_url, messages.length + 1);
        }
        
        toast.success('✅ Respuesta recibida');
      } else {
        // Just transcribe for text modes
        const response = await axios.post(
          `${API}/tutor/transcribe`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        // Set the transcribed text as input
        setInput(response.data.text);
        toast.success('🎤 Transcripción completada. Pulsa enviar o edita el texto.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al procesar audio';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (customPrompt = null) => {
    const messageText = customPrompt || input;
    if (!messageText.trim()) return;

    // Get the credit cost for current mode
    const currentMode = INTERACTION_MODES.find(m => m.id === interactionMode);
    const creditCost = currentMode?.cost || 5;
    
    // Check if user has enough credits (unless unlimited)
    if (aiCredits && !aiCredits.is_unlimited && aiCredits.balance < creditCost) {
      setUpsellData({
        required: creditCost,
        message: `Necesitas ${creditCost} créditos para esta acción. Tu saldo actual es ${aiCredits.balance}.`
      });
      setShowUpsellModal(true);
      return;
    }

    const userMessage = { role: 'user', content: messageText, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    const wantAudioResponse = interactionMode === 'text_with_audio' || interactionMode === 'voice_only';

    try {
      const token = localStorage.getItem('session_token');
      
      // First, use credits
      try {
        await axios.post(
          `${API}/ai-credits/use`,
          { 
            credits: creditCost, 
            action: `tutor_${interactionMode}`,
            details: { message_preview: messageText.substring(0, 50) }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (creditError) {
        if (creditError.response?.status === 402) {
          // Insufficient credits - show upsell
          const detail = creditError.response.data.detail;
          setUpsellData({
            required: detail.required || creditCost,
            message: detail.message || 'Créditos insuficientes'
          });
          setShowUpsellModal(true);
          setMessages(prev => prev.slice(0, -1)); // Remove the user message we just added
          setLoading(false);
          return;
        }
        // If it's another error, continue anyway (credits might not be configured)
        console.warn('Credit deduction failed, continuing anyway:', creditError);
      }
      
      const response = await axios.post(
        `${API}/tutor/chat`,
        { 
          message: messageText,
          include_audio: wantAudioResponse,
          context: {
            profession: user?.selected_profession,
            region: user?.selected_region
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const aiMessage = { 
        role: 'assistant', 
        content: response.data.response,
        audio_url: response.data.audio_url,
        credits_used: creditCost,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiMessage]);
      setCreditsUsed(prev => prev + creditCost);
      
      // Refresh credits balance
      refetchCredits();
      
      // Auto-play audio if enabled and available
      if (wantAudioResponse && response.data.audio_url) {
        playAudio(response.data.audio_url, messages.length);
      } else if (wantAudioResponse && !response.data.audio_url) {
        toast.info('Audio no disponible. El administrador debe configurar ElevenLabs.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al conectar con el tutor';
      toast.error(errorMsg);
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Lo siento, ha ocurrido un error. Por favor, intenta de nuevo.',
        isError: true,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const generateAudio = async (text, messageIndex) => {
    setGeneratingAudio(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/tutor/generate-audio`,
        { text: text.substring(0, 1000) },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const audioUrl = URL.createObjectURL(response.data);
      
      setMessages(prev => prev.map((msg, idx) => 
        idx === messageIndex ? { ...msg, audio_url: audioUrl } : msg
      ));
      
      playAudio(audioUrl, messageIndex);
      toast.success('Audio generado');
    } catch (error) {
      toast.error('Error al generar audio');
    } finally {
      setGeneratingAudio(false);
    }
  };

  const playAudio = (url, messageIndex) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    // Handle both relative URLs and blob URLs
    const audioUrl = url.startsWith('blob:') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    
    audio.onplay = () => setPlayingAudio(messageIndex);
    audio.onended = () => setPlayingAudio(null);
    audio.onerror = () => {
      setPlayingAudio(null);
      toast.error('Error al reproducir audio');
    };
    
    audio.play();
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingAudio(null);
  };

  const handleTopicClick = (topic) => {
    setInput(topic.prompt);
  };

  const selectMode = (modeId) => {
    setInteractionMode(modeId);
    setShowModeSelector(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentMode = INTERACTION_MODES.find(m => m.id === interactionMode);
  const isVoiceInput = interactionMode === 'voice_only' || interactionMode === 'voice_with_text';

  // Don't render anything if still checking access or user doesn't have tutor access
  if (checkingAccess) {
    return null;
  }
  
  // Don't show on excluded paths (landing page, auth, public pages)
  if (excludedPaths.includes(location.pathname)) {
    return null;
  }
  
  // If user is not logged in or doesn't have tutor access, don't show the tutor
  if (!hasTutorAccess) {
    return null;
  }

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-6 w-[420px] h-[600px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col z-50"
            data-testid="tutor-chat-modal"
          >
            {/* Mode Selector Screen */}
            {showModeSelector && messages.length === 0 ? (
              <div className="flex flex-col h-full">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-t-2xl">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="font-bold block">Tutor IA TuPlazaIA</span>
                        <span className="text-xs opacity-80">Elige cómo quieres comunicarte</span>
                      </div>
                    </div>
                    <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded-full p-2">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                <div className="flex-1 p-6 bg-slate-50 flex flex-col">
                  <div className="text-center mb-6">
                    <LinceMascot size={80} mood="happy" className="mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-slate-800">¿Cómo prefieres comunicarte?</h3>
                    <p className="text-sm text-slate-500 mt-1">Selecciona tu modo de interacción</p>
                  </div>
                  
                  <div className="space-y-3 flex-1">
                    {INTERACTION_MODES.map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => selectMode(mode.id)}
                        className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                          interactionMode === mode.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-slate-200 bg-white hover:border-indigo-300'
                        }`}
                      >
                        <span className="text-3xl">{mode.icon}</span>
                        <div className="text-left flex-1">
                          <p className="font-semibold text-slate-800">{mode.label}</p>
                          <p className="text-xs text-slate-500">{mode.desc}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-indigo-600">{mode.cost} créd</span>
                          <p className="text-xs text-slate-400">/mensaje</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  <Button
                    onClick={() => setShowModeSelector(false)}
                    className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-3"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Comenzar con {currentMode?.label}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-t-2xl">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="font-bold block">Tutor IA TuPlazaIA</span>
                        <span className="text-xs opacity-80">{currentMode?.icon} {currentMode?.label}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setShowSettingsModal(true)}
                        className="p-2 rounded-full transition-colors bg-white/20 hover:bg-white/30"
                        title="Cambiar modo"
                        data-testid="tutor-settings-btn"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded-full p-2" data-testid="close-tutor-btn">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Credits indicator */}
                  <div className="mt-3 text-xs bg-white/10 rounded-lg px-3 py-1.5 flex items-center justify-between">
                    <span className="flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      Saldo: {aiCredits?.is_unlimited ? '∞' : (aiCredits?.balance || 0)} créditos
                    </span>
                    <span className="opacity-60">{currentMode?.cost} créd/msg</span>
                  </div>
                  
                  {/* Low balance warning */}
                  {aiCredits && !aiCredits.is_unlimited && aiCredits.balance < 20 && (
                    <div className={`mt-2 text-xs px-3 py-1.5 rounded-lg flex items-center gap-2 ${
                      aiCredits.balance < 5 ? 'bg-red-500/20 text-red-100' : 'bg-amber-500/20 text-amber-100'
                    }`}>
                      <Zap className="w-3 h-3" />
                      {aiCredits.balance < 5 
                        ? '¡Créditos casi agotados!' 
                        : 'Créditos bajos'}
                      <button 
                        onClick={() => setShowUpsellModal(true)}
                        className="ml-auto underline hover:no-underline"
                      >
                        Recargar
                      </button>
                    </div>
                  )}
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                  {messages.length === 0 && (
                    <div className="text-center py-4">
                      <LinceMascot size={60} mood="happy" className="mx-auto mb-4" />
                      <p className="font-bold text-slate-800 mb-2">¡Hola! Soy tu Tutor IA</p>
                      <p className="text-sm text-slate-600 mb-4">
                        {isVoiceInput 
                          ? 'Pulsa el micrófono y háblame tu duda' 
                          : 'Escribe tu pregunta o elige un tema'}
                      </p>
                      
                      {/* Quick topics */}
                      <div className="grid grid-cols-2 gap-2 text-left">
                        {TUTOR_TOPICS.map((topic) => {
                          const Icon = topic.icon;
                          return (
                            <button
                              key={topic.id}
                              onClick={() => handleTopicClick(topic)}
                              className="flex items-center gap-2 p-3 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all text-sm"
                            >
                              <Icon className="w-4 h-4 text-indigo-600" />
                              <span className="text-slate-700">{topic.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {messages.map((msg, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`chat-message-${index}`}
                    >
                      <div className={`max-w-[85%]`}>
                        <div
                          className={`p-3 rounded-2xl ${
                            msg.role === 'user'
                              ? 'bg-indigo-600 text-white rounded-br-sm'
                              : msg.isError
                                ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-sm'
                                : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                          }`}
                        >
                          {msg.isVoice && (
                            <div className="flex items-center gap-1 text-xs opacity-70 mb-1">
                              <Mic className="w-3 h-3" /> Mensaje de voz
                            </div>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                        
                        {/* Audio controls for AI messages */}
                        {msg.role === 'assistant' && !msg.isError && (
                          <div className="mt-2 flex items-center gap-2">
                            {msg.audio_url ? (
                              <button
                                onClick={() => playingAudio === index ? stopAudio() : playAudio(msg.audio_url, index)}
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                  playingAudio === index
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                                }`}
                              >
                                {playingAudio === index ? (
                                  <>
                                    <Square className="w-3 h-3" />
                                    Detener
                                  </>
                                ) : (
                                  <>
                                    <Play className="w-3 h-3" />
                                    Escuchar
                                  </>
                                )}
                              </button>
                            ) : (
                              <button
                                onClick={() => generateAudio(msg.content, index)}
                                disabled={generatingAudio}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors disabled:opacity-50"
                              >
                                {generatingAudio ? (
                                  <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Generando...
                                  </>
                                ) : (
                                  <>
                                    <Volume2 className="w-3 h-3" />
                                    Generar audio
                                  </>
                                )}
                              </button>
                            )}
                            
                            <span className="text-xs text-slate-400">
                              {msg.credits_used && `${msg.credits_used} crédito${msg.credits_used > 1 ? 's' : ''}`}
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                  
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm p-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                          <span className="text-sm text-slate-500">
                            {isRecording ? 'Procesando audio...' : 'El tutor está pensando...'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 border-t border-slate-200 bg-white rounded-b-2xl">
                  {isVoiceInput ? (
                    /* Voice Input Mode */
                    <div className="flex flex-col items-center gap-3">
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={loading}
                        className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                          isRecording
                            ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                            : 'bg-indigo-600 hover:bg-indigo-700'
                        } text-white disabled:opacity-50`}
                      >
                        {isRecording ? (
                          <Square className="w-6 h-6" />
                        ) : (
                          <Mic className="w-6 h-6" />
                        )}
                      </button>
                      <p className="text-sm text-slate-500">
                        {isRecording 
                          ? `🔴 Grabando ${formatTime(recordingTime)}... Pulsa para enviar`
                          : 'Pulsa para hablar'
                        }
                      </p>
                      {/* Also allow text input */}
                      <div className="w-full flex gap-2 mt-2">
                        <input
                          type="text"
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && !loading && handleSend()}
                          placeholder="O escribe aquí..."
                          className="flex-1 px-4 py-2 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none text-sm"
                          disabled={loading || isRecording}
                        />
                        {input && (
                          <Button
                            onClick={() => handleSend()}
                            disabled={loading || !input.trim()}
                            className="px-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Text Input Mode */
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && !loading && handleSend()}
                        placeholder="Pregunta al tutor..."
                        className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none text-sm"
                        disabled={loading}
                        data-testid="tutor-input"
                      />
                      <Button
                        onClick={() => handleSend()}
                        disabled={loading || !input.trim()}
                        className="px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="tutor-send-btn"
                      >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full shadow-lg flex items-center justify-center z-50"
        data-testid="tutor-toggle-btn"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>
      
      {/* Credit Upsell Modal */}
      <CreditUpsellModal
        isOpen={showUpsellModal}
        onClose={() => setShowUpsellModal(false)}
        currentBalance={aiCredits?.balance || 0}
        requiredCredits={upsellData.required}
        message={upsellData.message}
        onPurchaseSuccess={() => {
          refetchCredits();
          setShowUpsellModal(false);
        }}
      />
      
      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowSettingsModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-t-2xl flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5" />
                <span className="font-bold">Configuración del Tutor</span>
              </div>
              <button onClick={() => setShowSettingsModal(false)} className="hover:bg-white/20 rounded-full p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Modo de interacción</h3>
              <div className="space-y-3">
                {INTERACTION_MODES.map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => {
                      selectMode(mode.id);
                      setShowSettingsModal(false);
                    }}
                    className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                      interactionMode === mode.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 bg-white hover:border-indigo-300'
                    }`}
                  >
                    <span className="text-2xl">{mode.icon}</span>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800">{mode.label}</p>
                      <p className="text-xs text-slate-500">{mode.desc}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-indigo-600">{mode.cost}</span>
                      <p className="text-xs text-slate-400">créd/msg</p>
                    </div>
                  </button>
                ))}
              </div>
              
              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Créditos disponibles:</span>
                  <span className="font-bold text-indigo-600">
                    {aiCredits?.is_unlimited ? '∞ Ilimitados' : `${aiCredits?.balance || 0} créditos`}
                  </span>
                </div>
                {!aiCredits?.is_unlimited && aiCredits?.balance < 20 && (
                  <Button 
                    onClick={() => { setShowSettingsModal(false); setShowUpsellModal(true); }}
                    className="w-full mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Recargar créditos
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TutorChat;
